/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import debounce from 'lodash/debounce';
import { ChunkManager } from 'neuroglancer/chunk_manager/frontend';
import './data_panel_layout.less';
import { DisplayContext } from 'neuroglancer/display_context';
import { LayerManager, MouseSelectionState, SelectedLayerState, TrackableDataSelectionState } from 'neuroglancer/layer';
import * as L from 'neuroglancer/layout';
import { DisplayPose, LinkedOrientationState, LinkedPosition, linkedStateLegacyJsonView, LinkedZoomState, NavigationState, OrientationState, TrackableZoomInterface } from 'neuroglancer/navigation_state';
import { PerspectivePanel } from 'neuroglancer/perspective_view/panel';
import { RenderedDataPanel } from 'neuroglancer/rendered_data_panel';
import { RenderLayerRole } from 'neuroglancer/renderlayer';
import { SliceView } from 'neuroglancer/sliceview/frontend';
import { SliceViewerState, SliceViewPanel } from 'neuroglancer/sliceview/panel';
import { TrackableBoolean } from 'neuroglancer/trackable_boolean';
import { TrackableValue, WatchableSet, WatchableValueInterface } from 'neuroglancer/trackable_value';
import { TrackableRGB } from 'neuroglancer/util/color';
import { Borrowed, Owned, RefCounted } from 'neuroglancer/util/disposable';
import { removeChildren, removeFromParent } from 'neuroglancer/util/dom';
import { EventActionMap, registerActionListener } from 'neuroglancer/util/event_action_map';
import {quat} from 'neuroglancer/util/geom';
// import {packColor} from 'neuroglancer/util/color';
import { verifyObject, verifyObjectProperty, verifyPositiveInt } from 'neuroglancer/util/json';
import { NullarySignal } from 'neuroglancer/util/signal';
import { optionallyRestoreFromJsonMember, Trackable } from 'neuroglancer/util/trackable';
import { WatchableMap } from 'neuroglancer/util/watchable_map';
import { VisibilityPrioritySpecification } from 'neuroglancer/viewer_state';
import { DisplayDimensionsWidget } from 'neuroglancer/widget/display_dimensions_widget';
import { ScaleBarOptions } from 'neuroglancer/widget/scale_bar';
import { viewer as _layoutviewer } from 'src/main';
// import { Annotation, makeSingleLineAnnotation, makeSinglePointAnnotation, Point } from './annotation';
// import { LoadedDataSubsource, LoadedLayerDataSource } from './layer_data_source';
// import { LayerGroupViewer } from './layer_group_viewer';
// import { message } from './widget/message';
// import { PlaceBoundingBoxTool } from './ui/annotations';
// import { AnnotationUserLayer } from './annotation/user_layer';
// import { MessageBox } from './widget/message_box';

export let currentActivedTool1: HTMLElement | null = null
export interface SliceViewViewerState {
  chunkManager: ChunkManager;
  navigationState: NavigationState;
  layerManager: LayerManager;
  wireFrame: WatchableValueInterface<boolean>;
  panelType?: string;
}

export class InputEventBindings {
  perspectiveView = new EventActionMap();
  sliceView = new EventActionMap();
}

export interface ViewerUIState extends SliceViewViewerState, VisibilityPrioritySpecification {
  display: DisplayContext;
  mouseState: MouseSelectionState;
  perspectiveNavigationState: NavigationState;
  selectionDetailsState: TrackableDataSelectionState;
  showPerspectiveSliceViews: TrackableBoolean;
  showAxisLines: TrackableBoolean;
  wireFrame: TrackableBoolean;
  showScaleBar: TrackableBoolean;
  scaleBarOptions: TrackableValue<ScaleBarOptions>;
  visibleLayerRoles: WatchableSet<RenderLayerRole>;
  selectedLayer: SelectedLayerState;
  inputEventBindings: InputEventBindings;
  crossSectionBackgroundColor: TrackableRGB;
  perspectiveViewBackgroundColor: TrackableRGB;
}

export interface DataDisplayLayout extends RefCounted {
  rootElement: HTMLElement;
  container: DataPanelLayoutContainer;
}

type NamedAxes = 'xy'|'xz'|'yz';

/** canvas about lasso   */
// let clickCount = 0;   // 配准count
// let all_Reference_id: Array<string | Array<string>> = [];                   //  存储所有id
// let boundCount = 0;

const AXES_RELATIVE_ORIENTATION = new Map<NamedAxes, quat|undefined>([
  ['xy', undefined],
  ['xz', quat.rotateX(quat.create(), quat.create(), Math.PI / 2)],
  ['yz', quat.rotateY(quat.create(), quat.create(), Math.PI / 2)],
]);

const oneSquareSymbol = '◻';

const LAYOUT_SYMBOLS = new Map<string, string>([
  ['4panel', '◱'],
  ['3d', oneSquareSymbol],
]);

export function makeSliceView(viewerState: SliceViewViewerState, baseToSelf?: quat) {
  let navigationState: NavigationState;
  if (baseToSelf === undefined) {
    navigationState = viewerState.navigationState.addRef();
  } else {
    navigationState = new NavigationState(
        new DisplayPose(
            viewerState.navigationState.pose.position.addRef(),
            viewerState.navigationState.pose.displayDimensionRenderInfo.addRef(),
            OrientationState.makeRelative(
                viewerState.navigationState.pose.orientation, baseToSelf)),
        viewerState.navigationState.zoomFactor.addRef(),
        viewerState.navigationState.depthRange.addRef());
  }
  return new SliceView(
      viewerState.chunkManager, viewerState.layerManager, navigationState, viewerState.wireFrame, viewerState.panelType);
}

export function makeNamedSliceView(viewerState: SliceViewViewerState, axes: NamedAxes) {
  return makeSliceView(viewerState, AXES_RELATIVE_ORIENTATION.get(axes)!);
}

export function makeOrthogonalSliceViews(viewerState: SliceViewViewerState) {
  return new Map<NamedAxes, SliceView>([
    ['xy', makeNamedSliceView(viewerState, 'xy')],
    ['xz', makeNamedSliceView(viewerState, 'xz')],
    ['yz', makeNamedSliceView(viewerState, 'yz')],
  ]);
}

export function getCommonViewerState(viewer: ViewerUIState) {
  return {
    crossSectionBackgroundColor: viewer.crossSectionBackgroundColor,
    perspectiveViewBackgroundColor: viewer.perspectiveViewBackgroundColor,
    selectionDetailsState: viewer.selectionDetailsState,
    mouseState: viewer.mouseState,
    layerManager: viewer.layerManager,
    showAxisLines: viewer.showAxisLines,
    wireFrame: viewer.wireFrame,
    visibleLayerRoles: viewer.visibleLayerRoles,
    selectedLayer: viewer.selectedLayer,
    visibility: viewer.visibility,
    scaleBarOptions: viewer.scaleBarOptions,
    layerSelectedValues: viewer.selectionDetailsState.layerSelectedValues
  };
}

function getCommonPerspectiveViewerState(container: DataPanelLayoutContainer) {
  const {viewer} = container;
  return {
    ...getCommonViewerState(viewer),
    navigationState: viewer.perspectiveNavigationState,
    inputEventMap: viewer.inputEventBindings.perspectiveView,
    orthographicProjection: container.specification.orthographicProjection,
    showScaleBar: viewer.showScaleBar,
    rpc: viewer.chunkManager.rpc!,
  };
}

function getCommonSliceViewerState(viewer: ViewerUIState) {
  return {
    ...getCommonViewerState(viewer),
    navigationState: viewer.navigationState,
    inputEventMap: viewer.inputEventBindings.sliceView,
  };
}

function addDisplayDimensionsWidget(layout: DataDisplayLayout, panel: RenderedDataPanel) {
  const {navigationState} = panel;
  panel.element.appendChild(
      layout
          .registerDisposer(new DisplayDimensionsWidget(
              navigationState.pose.displayDimensionRenderInfo.addRef(), navigationState.zoomFactor,
              navigationState.depthRange.addRef(), (panel instanceof SliceViewPanel) ? 'px' : 'vh'))
          .element);
}

// function showMessageBox(title: string, content:Node){
//   let message = new MessageBox(title, content, makeElement('input',['el-button', 'lasso-message-continue','el-button--default','el-button--small'],{'type': 'button','value':'Continue'}) as HTMLInputElement);
//   message.element.style.display = 'block';
//   return message
// }

function registerRelatedLayouts(
  layout: DataDisplayLayout, panel: RenderedDataPanel, relatedLayouts: string[]) {
const controls = document.createElement('div');
controls.className = 'neuroglancer-data-panel-layout-controls';
layout.registerDisposer(() => removeFromParent(controls));
for (let i = 0; i < 2; ++i) {
  const relatedLayout = relatedLayouts[Math.min(relatedLayouts.length - 1, i)];
  layout.registerDisposer(registerActionListener(
      panel.element, i === 0 ? 'toggle-layout' : 'toggle-layout-alternative', (event: Event) => {
        layout.container.name = relatedLayout;
        event.stopPropagation();
      }));
}
for (const relatedLayout of relatedLayouts) {
  const button = document.createElement('button');
  const innerDiv = document.createElement('div');
  button.appendChild(innerDiv);
  innerDiv.textContent = LAYOUT_SYMBOLS.get(relatedLayout)!;
  button.title = `Switch to ${relatedLayout} layout.`;
  button.style.display = 'none';
  button.addEventListener('click', () => {
    layout.container.name = relatedLayout;
    if(layout.container.name === 'xy'){
      (document.getElementsByClassName('colorBarScale')[0] as HTMLElement).style.display = 'block';
      let c = document.createElement('canvas');
      c.id = 'lassoCanvas';
      document.getElementsByClassName('neuroglancer-rendered-data-panel')[0].appendChild(c)
      // document.getElementsByClassName('neuroglancer-viewer-scalebar')[0].appendChild(c)
    }
  });
  controls.appendChild(button);
}
panel.element.appendChild(controls);
}


function makeSliceViewFromSpecification(
    viewer: SliceViewViewerState, specification: Borrowed<CrossSectionSpecification>) {
  const sliceView = new SliceView(
      viewer.chunkManager, viewer.layerManager, specification.navigationState.addRef(),
      viewer.wireFrame);
  const updateViewportSize = () => {
    const {width: {value: width}, height: {value: height}} = specification;
    sliceView.projectionParameters.setViewport({
      width,
      height,
      logicalWidth: width,
      logicalHeight: height,
      visibleLeftFraction: 0,
      visibleTopFraction: 0,
      visibleWidthFraction: 1,
      visibleHeightFraction: 1
    });
  };
  sliceView.registerDisposer(specification.width.changed.add(updateViewportSize));
  sliceView.registerDisposer(specification.height.changed.add(updateViewportSize));
  updateViewportSize();
  return sliceView;
}

function addUnconditionalSliceViews(
    viewer: SliceViewViewerState, panel: PerspectivePanel,
    crossSections: Borrowed<CrossSectionSpecificationMap>) {
  const previouslyAdded = new Map<Borrowed<CrossSectionSpecification>, Borrowed<SliceView>>();
  const update = () => {
    const currentCrossSections = new Set<Borrowed<CrossSectionSpecification>>();
    // Add missing cross sections.
    for (const crossSection of crossSections.values()) {
      currentCrossSections.add(crossSection);
      if (previouslyAdded.has(crossSection)) {
        continue;
      }
      const sliceView = makeSliceViewFromSpecification(viewer, crossSection);
      panel.sliceViews.set(sliceView, true);
      previouslyAdded.set(crossSection, sliceView);
    }
    // Remove extra cross sections.
    for (const [crossSection, sliceView] of previouslyAdded) {
      if (currentCrossSections.has(crossSection)) {
        continue;
      }
      panel.sliceViews.delete(sliceView);
    }
  };
  update();
}

export class FourPanelLayout extends RefCounted {
  constructor(
      public container: DataPanelLayoutContainer, public rootElement: HTMLElement,
      public viewer: ViewerUIState, crossSections: Borrowed<CrossSectionSpecificationMap>) {
    super();

    let sliceViews = makeOrthogonalSliceViews(viewer);
    let {display} = viewer;

    const perspectiveViewerState = {
      ...getCommonPerspectiveViewerState(container),
      showSliceViews: viewer.showPerspectiveSliceViews,
      showSliceViewsCheckbox: true,
    };

    const sliceViewerState = {
      ...getCommonSliceViewerState(viewer),
      showScaleBar: viewer.showScaleBar,
    };
    const sliceViewerStateWithoutScaleBar = {
      ...getCommonSliceViewerState(viewer),
      showScaleBar: new TrackableBoolean(false, false),
    };

    const makeSliceViewPanel =
        (axes: NamedAxes, element: HTMLElement, state: SliceViewerState,
         displayDimensionsWidget: boolean) => {
          const panel = this.registerDisposer(
              new SliceViewPanel(display, element, sliceViews.get(axes)!, state));
          if (displayDimensionsWidget) {
            addDisplayDimensionsWidget(this, panel);
          }
          registerRelatedLayouts(this, panel, [axes, `${axes}-3d`]);
          return panel;
        };
    let mainDisplayContents = [
      L.withFlex(1, L.box('column', [
        L.withFlex(1, L.box('row', [
          L.withFlex(1, element => {
            // 
            makeSliceViewPanel('xy', element, sliceViewerState, true);
          }),
          L.withFlex(1, element => {
            makeSliceViewPanel('xz', element, sliceViewerStateWithoutScaleBar, false);
          })
        ])),
        L.withFlex(1, L.box('row', [
          L.withFlex(1, element => {
            let panel = this.registerDisposer(
                new PerspectivePanel(display, element, perspectiveViewerState));
            for (let sliceView of sliceViews.values()) {
              panel.sliceViews.set(sliceView.addRef(), false);
            }
            addDisplayDimensionsWidget(this, panel);
            addUnconditionalSliceViews(viewer, panel, crossSections);
            registerRelatedLayouts(this, panel, ['3d']);
          }),
          L.withFlex(1, element => {
            makeSliceViewPanel('yz', element, sliceViewerStateWithoutScaleBar, false);
          })
        ])),
      ]))
    ];
    L.box('row', mainDisplayContents)(rootElement);
  }

  disposed() {
    removeChildren(this.rootElement);
    super.disposed();
  }
}

export class SliceViewPerspectiveTwoPanelLayout extends RefCounted {
  constructor(
      public container: DataPanelLayoutContainer, public rootElement: HTMLElement,
      public viewer: ViewerUIState, public direction: 'row'|'column', axes: NamedAxes,
      crossSections: Borrowed<CrossSectionSpecificationMap>) {
    super();

    let sliceView = makeNamedSliceView(viewer, axes);
    let {display} = viewer;

    const perspectiveViewerState = {
      ...getCommonPerspectiveViewerState(container),
      showSliceViews: viewer.showPerspectiveSliceViews,
      showSliceViewsCheckbox: true,
    };

    const sliceViewerState = {
      ...getCommonSliceViewerState(viewer),
      showScaleBar: viewer.showScaleBar,
    };

    L.withFlex(1, L.box(direction, [
      L.withFlex(
          1,
          element => {
            const panel = this.registerDisposer(
                new SliceViewPanel(display, element, sliceView, sliceViewerState));
            addDisplayDimensionsWidget(this, panel);
            registerRelatedLayouts(this, panel, [axes, '4panel']);
          }),
      L.withFlex(
          1,
          element => {
            let panel = this.registerDisposer(
                new PerspectivePanel(display, element, perspectiveViewerState));
            panel.sliceViews.set(sliceView.addRef(), false);
            addUnconditionalSliceViews(viewer, panel, crossSections);
            addDisplayDimensionsWidget(this, panel);
            registerRelatedLayouts(this, panel, ['3d', '4panel']);
          }),
    ]))(rootElement);
  }

  disposed() {
    removeChildren(this.rootElement);
    super.disposed();
  }
}

export class SinglePanelLayout extends RefCounted {
  constructor(
      public container: DataPanelLayoutContainer, public rootElement: HTMLElement,
      public viewer: ViewerUIState, axes: NamedAxes) {
    super();
    let sliceView = makeNamedSliceView(viewer, axes);
    const sliceViewerState = {
      ...getCommonSliceViewerState(viewer),
      showScaleBar: viewer.showScaleBar,
    };
    L.box('row', [L.withFlex(1, element => {
            const panel = this.registerDisposer(
                new SliceViewPanel(viewer.display, element, sliceView, sliceViewerState));
            addDisplayDimensionsWidget(this, panel);
            registerRelatedLayouts(this, panel, ['4panel', `${axes}-3d`]);
          })])(rootElement);
  }

  disposed() {
    removeChildren(this.rootElement);
    super.disposed();
  }
}

export class SinglePerspectiveLayout extends RefCounted {
  constructor(
      public container: DataPanelLayoutContainer, public rootElement: HTMLElement,
      public viewer: ViewerUIState, crossSections: Borrowed<CrossSectionSpecificationMap>) {
    super();
    let perspectiveViewerState = {
      ...getCommonPerspectiveViewerState(container),
      showSliceViews: new TrackableBoolean(false, false),
    };


    L.box('row', [L.withFlex(1, element => {
            const panel = this.registerDisposer(
                new PerspectivePanel(viewer.display, element, perspectiveViewerState));
            addUnconditionalSliceViews(viewer, panel, crossSections);
            addDisplayDimensionsWidget(this, panel);
            registerRelatedLayouts(this, panel, ['4panel']);
          })])(rootElement);
  }

  disposed() {
    removeChildren(this.rootElement);
    super.disposed();
  }
}

export const LAYOUTS = new Map<string, {
  factory:
      (container: DataPanelLayoutContainer, element: HTMLElement, viewer: ViewerUIState,
       crossSections: Borrowed<CrossSectionSpecificationMap>) => DataDisplayLayout
}>(
    [
      [
        'xy', {
          factory: (container, element, viewer, crossSections) =>
              new FourPanelLayout(container, element, viewer, crossSections)
        }
      ],
      [
        '2d', {
          factory: (container, element, viewer, crossSections) =>
              new SinglePerspectiveLayout(container, element, viewer, crossSections)
        }
      ],
    ],
);

for (const axes of AXES_RELATIVE_ORIENTATION.keys()) {
  LAYOUTS.set(axes, {
    factory: (container, element, viewer) =>
        new SinglePanelLayout(container, element, viewer, <NamedAxes>axes)
  });
  const splitLayout = `${axes}-3d`;
  LAYOUT_SYMBOLS.set(axes, oneSquareSymbol);
  LAYOUT_SYMBOLS.set(splitLayout, '◫');
  LAYOUTS.set(splitLayout, {
    factory: (container, element, viewer, crossSections) => new SliceViewPerspectiveTwoPanelLayout(
        container, element, viewer, 'row', <NamedAxes>axes, crossSections)
  });
}

export function getLayoutByName(obj: any) {
  let layout = LAYOUTS.get(obj);
  if (layout === undefined) {
    throw new Error(`Invalid layout name: ${JSON.stringify(obj)}.`);
  }
  return layout;
}

export function validateLayoutName(obj: any) {
  getLayoutByName(obj);
  return <string>obj;
}

export class CrossSectionSpecification extends RefCounted implements Trackable {
  width = new TrackableValue<number>(1000, verifyPositiveInt);
  height = new TrackableValue<number>(1000, verifyPositiveInt);
  position: LinkedPosition;
  orientation: LinkedOrientationState;
  scale: LinkedZoomState<TrackableZoomInterface>;
  navigationState: NavigationState;
  changed = new NullarySignal();
  constructor(parent: Borrowed<NavigationState>) {
    super();
    this.position = new LinkedPosition(parent.position.addRef());
    this.position.changed.add(this.changed.dispatch);
    this.orientation = new LinkedOrientationState(parent.pose.orientation.addRef());
    this.orientation.changed.add(this.changed.dispatch);
    this.width.changed.add(this.changed.dispatch);
    this.height.changed.add(this.changed.dispatch);
    this.scale = new LinkedZoomState(
        parent.zoomFactor.addRef(), parent.zoomFactor.displayDimensionRenderInfo.addRef());
    this.scale.changed.add(this.changed.dispatch);
    this.navigationState = this.registerDisposer(new NavigationState(
        new DisplayPose(
            this.position.value, parent.pose.displayDimensionRenderInfo.addRef(),
            this.orientation.value),
        this.scale.value, parent.depthRange.addRef()));
  }

  restoreState(obj: any) {
    verifyObject(obj);
    optionallyRestoreFromJsonMember(obj, 'width', this.width);
    optionallyRestoreFromJsonMember(obj, 'height', this.height);
    optionallyRestoreFromJsonMember(obj, 'position', linkedStateLegacyJsonView(this.position));
    optionallyRestoreFromJsonMember(obj, 'orientation', this.orientation);
    optionallyRestoreFromJsonMember(obj, 'scale', this.scale);
    optionallyRestoreFromJsonMember(obj, 'zoom', linkedStateLegacyJsonView(this.scale));
  }

  reset() {
    this.width.reset();
    this.height.reset();
    this.position.reset();
    this.orientation.reset();
    this.scale.reset();
  }

  toJSON() {
    return {
      width: this.width.toJSON(),
      height: this.height.toJSON(),
      position: this.position.toJSON(),
      orientation: this.orientation.toJSON(),
      scale: this.scale.toJSON(),
    };
  }
}

export class CrossSectionSpecificationMap extends WatchableMap<string, CrossSectionSpecification> {
  constructor(private parentNavigationState: Owned<NavigationState>) {
    super(
        (context, spec) => context.registerDisposer(
            context.registerDisposer(spec).changed.add(this.changed.dispatch)),
    );
    this.registerDisposer(parentNavigationState);
  }

  restoreState(obj: any) {
    verifyObject(obj);
    for (const key of Object.keys(obj)) {
      const state = new CrossSectionSpecification(this.parentNavigationState);
      try {
        this.set(key, state.addRef());
        state.restoreState(obj[key]);
      } finally {
        state.dispose();
      }
    }
  }

  reset() {
    this.clear();
  }

  toJSON() {
    if (this.size === 0) return undefined;
    const obj: {[key: string]: any} = {};
    for (const [k, v] of this) {
      obj[k] = v.toJSON();
    }
    return obj;
  }
}

export class DataPanelLayoutSpecification extends RefCounted implements Trackable {
  changed = new NullarySignal();
  type: TrackableValue<string>;
  crossSections: CrossSectionSpecificationMap;
  orthographicProjection = new TrackableBoolean(false);

  constructor(parentNavigationState: Owned<NavigationState>, defaultLayout: string) {
    super();
    this.type = new TrackableValue<string>(defaultLayout, validateLayoutName);
    this.type.changed.add(this.changed.dispatch);
    this.crossSections =
        this.registerDisposer(new CrossSectionSpecificationMap(parentNavigationState.addRef()));
    this.crossSections.changed.add(this.changed.dispatch);
    this.orthographicProjection.changed.add(this.changed.dispatch);
    this.registerDisposer(parentNavigationState);
  }

  reset() {
    this.crossSections.clear();
    this.orthographicProjection.reset();
    this.type.reset();
  }

  restoreState(obj: any) {
    this.crossSections.clear();
    this.orthographicProjection.reset();
    if (typeof obj === 'string') {
      this.type.restoreState(obj);
    } else {
      verifyObject(obj);
      verifyObjectProperty(obj, 'type', x => this.type.restoreState(x));
      verifyObjectProperty(
          obj, 'orthographicProjection', x => this.orthographicProjection.restoreState(x));
      verifyObjectProperty(
          obj, 'crossSections', x => x !== undefined && this.crossSections.restoreState(x));
    }
  }

  toJSON() {
    const {type, crossSections, orthographicProjection} = this;
    const orthographicProjectionJson = orthographicProjection.toJSON();
    if (crossSections.size === 0 && orthographicProjectionJson === undefined) {
      return type.value;
    }
    return {
      type: type.value,
      crossSections: crossSections.toJSON(),
      orthographicProjection: orthographicProjectionJson,
    };
  }
}

export class DataPanelLayoutContainer extends RefCounted {
  element = document.createElement('div');
  specification: Owned<DataPanelLayoutSpecification>;

  private layout: DataDisplayLayout|undefined;

  get name() {
    return this.specification.type.value;
  }
  set name(value: string) {
    this.specification.type.value = value;
  }

  constructor(public viewer: ViewerUIState, defaultLayout: string) {
    super();
    this.specification = this.registerDisposer(
        new DataPanelLayoutSpecification(this.viewer.navigationState.addRef(), defaultLayout));
    this.element.style.flex = '1';
    this.element.classList.add('hhh');
    // let lineCount = 0;
    // let lineArr:any = [];
    // let positionArr:any = [];
    // let is_boundbox: boolean = false;   //  校验：无框选，不弹窗
    // let boxValue: PlaceBoundingBoxTool;
    // let annotationLayer: UserLayer | undefined;     //  展开描述框所用layer
    // let boundbox_size:any = [];
    // let source: LoadedDataSubsource = (this.viewer.layerManager.managedLayers[0]?.layer?.dataSources[0]?.loadState as LoadedLayerDataSource)?.subsources[2];
    registerActionListener(this.element, 'mouse-to-sign', ()=>{
      // if(document.getElementsByClassName('neuroglancer-icon-IF-control')[0].getAttribute('data-biaoji') !== 'anno'){
      //   // if配准时
      //   source = (getSource(this.viewer)).source as LoadedDataSubsource;
      //   const signFlag = document.getElementsByClassName('neuroglancer-icon-IF-control')[0]?.getAttribute('data-biaoji') ?? 'false';
      //   const isIFImage = document.getElementsByClassName('neuroglancer-icon-IF-control')[0]?.getAttribute('isIFImage') ?? 'false';
      //   if(isIFImage === 'true' && source.subsourceEntry.subsource.staticAnnotations?.annotationMap.size === 4 ){
      //     // IF配准只能有四个
      //     new message('There are already four marks！','error');
      //     return
      //   }else if( isIFImage === 'true' && signFlag !== 'point'){
      //     // IF配准只能是标记点
      //     new message('Please select a marker point！','error');
      //     return
      //   }else if(isIFImage === 'true'){
      //     let position = [...this.viewer.mouseState.position];
      //     if(this.viewer.layerManager.managedLayers[0].layer?.genename.includes(allToolsButton.isIFRegister as string)){
      //       position = get_real_position(position, [...source.loadedDataSource.dataSource.modelTransform.inputSpace.bounds.upperBounds] )
      //     }
      //     if(!positionArr.join().includes(position[0]) && !positionArr.join().includes(position[1])){
      //       clickCount ++;
      //       positionArr.push(position);
      //       let pointId = 'point' + clickCount;
      //       if(signFlag === 'line'){
      //         lineCount++;
      //         if(lineCount % 2 === 0){
      //           pointId = 'point_line' + (lineCount/2) + '_point' + clickCount;
      //           lineArr[lineCount/2 - 1].end = position;
      //           source.subsourceEntry.subsource.staticAnnotations?.add(makeSingleLineAnnotation(new Float32Array(lineArr[lineCount/2-1].start), new Float32Array(lineArr[lineCount/2-1].end), 'line'+lineCount/2));
      //         }else{
      //           pointId = 'point_line' + ( (lineCount + 1)/2 ) + '_point' + clickCount;
      //           lineArr.push({id: lineCount, start: position, end: ''})
      //         }
      //       }
      //       let newPoint = makeSinglePointAnnotation(new Float32Array(position), pointId);
      //       newPoint.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
      //       source.subsourceEntry.subsource.staticAnnotations?.add(newPoint);
      //     }else{}
      //   }
      // }else{
      // }
    })
    const scheduleUpdateLayout = this.registerCancellable(debounce(() => this.updateLayout(), 0));
    this.specification.type.changed.add(scheduleUpdateLayout);

    registerActionListener(
        this.element, 'toggle-orthographic-projection',
        () => this.specification.orthographicProjection.toggle());

    // Ensure the layout is updated before drawing begins to avoid flicker.
    this.registerDisposer(
        this.viewer.display.updateStarted.add(() => scheduleUpdateLayout.flush()));
    scheduleUpdateLayout();
  }
  get changed() {
    return this.specification.changed;
  }
  toJSON() {
    return this.specification.toJSON();
  }
  restoreState(obj: any) {
    this.specification.restoreState(obj);
  }
  reset() {
    this.specification.reset();
  }
  private disposeLayout() {
    let {layout} = this;
    if (layout !== undefined) {
      layout.dispose();
      this.layout = undefined;
    }
  }
  private updateLayout() {
    this.disposeLayout();
    this.layout = getLayoutByName(this.name).factory(
        this, this.element, this.viewer, this.specification.crossSections);
  }
  disposed() {
    this.disposeLayout();
    super.disposed();
  }
}
// 用于寻找selectLayer，并且和他的index序列
export function findSelectedImageLayer() {
  const layers = _layoutviewer.state.toJSON().layers;
  const selectLayer = _layoutviewer.state.toJSON().selectedLayer;
  let num:number = 0;
  const layer =  layers.find((item:any, index: number)=>{
    if(selectLayer.layer){
      num = index;
      return item.name === selectLayer.layer && item.type === 'image'
    }else{
      num = index;
      return item.type === 'image'
    }
  });
  return {layer: layer, num: num}
}

// function get_real_position(r: Array<number>, p: Array<number>){
//   let position = r;
//   switch(viewer.image_rotate){
//     case 'A':
//       position = [p[0] - r[1], r[0], r[2]];
//       break;
//     case 'AA':
//       position = [p[0] - r[0], p[1] - r[1], r[2]];
//       break;
//     case 'AAA':
//       position = [r[1], p[1] - r[0], r[2]];
//       break;
//     default:
//       position = r;
//       break;
//   }
//   return position
// }

// function getSource(viewer: ViewerUIState){
//   let source:LoadedDataSubsource | undefined = undefined;
//   let annotationLayer: UserLayer | undefined = undefined;
//   for( let value of (viewer as LayerGroupViewer).layerPanel?.layerManager.renderLayerToManagedLayerMap!){
//     if(value[1].name.toLocaleLowerCase().includes('ssdna') || value[1].name.toLocaleLowerCase().includes('_if')){
//       source = (value[1].layer?.dataSources[0].loadState as LoadedLayerDataSource).subsources[2];
//       annotationLayer = value[1].layer as UserLayer;
//       break;
//     }
//   }
//   return {source: source, annotationLayer: annotationLayer}
// }

// function change_delete_sign(source: LoadedDataSubsource, boundbox_size: any[], flag: boolean){
//   let referenceValue:any = {}; 
//   if(source?.subsourceEntry?.subsource?.staticAnnotations?.references){
//     const minX = Math.min(boundbox_size[1][0], boundbox_size[0][0]);
//     const maxX = Math.max(boundbox_size[1][0], boundbox_size[0][0]);
//     const minY = Math.min(boundbox_size[0][1], boundbox_size[1][1]);
//     const maxY = Math.max(boundbox_size[0][1], boundbox_size[1][1]);
//     for (let value_ssDNA of source?.subsourceEntry?.subsource?.staticAnnotations?.references) {
//       const reference = source.subsourceEntry.subsource.staticAnnotations.getReference(value_ssDNA[0]);
//       if( (value_ssDNA[1].value as Annotation)?.id.includes('point') ){
//         const point = (value_ssDNA[1].value as Point).point;
//         if( point[0] < maxX && point[0] > minX && point[1] < maxY && point[1] > minY){
//           if(!flag)source.subsourceEntry.subsource.staticAnnotations?.update(reference, {...value_ssDNA[1].value as Annotation, properties: [packColor(vec4.fromValues(1.0, 1.0, 1.0, 0.75))]} );
//           if(flag)source.subsourceEntry.subsource.staticAnnotations?.delete(reference);
//         }
//       }
//       referenceValue[value_ssDNA[0]] = value_ssDNA[1];
//     }
//   }
// }
